/**
 * The CRM viewer's current view (spec S1 and S6, notes 01 N-06, plan ruling
 * 29): which firm a Cova staff member is looking at through
 * `/super-admin/crm-viewer`, and, for See as, through whose eyes.
 *
 * NOT REACT STATE, for the reason `platform-scope-store.ts` gives: every
 * browser HTTP and RPC client is built at module load and hands its requests
 * to `scopedFetch`, which can read a module value and nothing else. So
 * `scopedFetch` reads this once per request.
 *
 * ONE SETTER. `ViewAsProvider` sets it DURING ITS RENDER, before its children's
 * first queries go out (a child's effect runs before its parent's, so an effect
 * would set it too late), and clears it when it unmounts. Nothing else calls
 * `setViewAs` (ratcheted by `bms-view-as-header-ratchet.test.ts`, half B), and
 * the scope barrel does not publish it, as it does not publish
 * `setCurrentScope`.
 *
 * NO SUBSCRIBERS. Nothing re-renders on it: the firm and See as change only by
 * a full document navigation (spec S9), which starts every module, this one
 * included, from nothing.
 *
 * THE ONE WRITER OF THE TWO HEADERS. Their names come from
 * `src/domain/scope/view-as.ts`; this module is the only browser code that
 * names them, and `scopedFetch` asks it to set them rather than naming them
 * itself. The server's admission is the one reader. A header here is a claim,
 * never an authority: the session middleware admits it only for a caller
 * holding `orgs.read`, and refuses every write whoever sends it.
 */
import { VIEW_AS_MEMBER_HEADER, VIEW_AS_ORG_HEADER } from "@/domain/scope/view-as"

export interface ViewAs {
  /** The viewed firm's organisation id, a lower-case UUID. */
  readonly orgId: string
  /** The See as member's Clerk id, or null for the firm administrator's view. */
  readonly memberId: string | null
}

let current: ViewAs | null = null

/** The view the next request should carry, or null when no viewer is open. */
export const getViewAs = (): ViewAs | null => current

/** Enter a view. Called by `ViewAsProvider` during its render; a repeat of the same view is a no-op. */
export const setViewAs = (view: ViewAs): void => {
  if (current !== null && current.orgId === view.orgId && current.memberId === view.memberId) return
  current = { orgId: view.orgId, memberId: view.memberId }
}

/** Leave the view: `ViewAsProvider`'s unmount, so no later request is sent as the firm. */
export const clearViewAs = (): void => {
  current = null
}

/**
 * THE VIEWER'S OWN QUERY CLIENTS (fix 3 finding 1). A query run by one of these
 * carries the view; a query run by any other client never does (the lane
 * rule in `scoped-fetch.ts`). Registered by `createViewerQueryClient`, held
 * weakly so a client left behind by a full navigation is collected.
 */
const viewerQueryClients = new WeakSet<object>()

/** Register a client as the viewer's own. Called once, by `createViewerQueryClient`. */
export const markViewerQueryClient = (client: object): void => {
  viewerQueryClients.add(client)
}

/** Whether a client is the viewer's own. */
export const isViewerQueryClient = (client: unknown): boolean =>
  typeof client === "object" && client !== null && viewerQueryClients.has(client)

/**
 * Set the view's two headers on a header bag, last, so a caller's own value for
 * either name never survives. The member header is absent for the
 * administrator's view, never empty.
 */
export const putViewAsHeaders = (headers: Headers, view: ViewAs): Headers => {
  headers.set(VIEW_AS_ORG_HEADER, view.orgId)
  if (view.memberId === null) headers.delete(VIEW_AS_MEMBER_HEADER)
  else headers.set(VIEW_AS_MEMBER_HEADER, view.memberId)
  return headers
}
